import React, { useState, useEffect } from "react";
import { getCandidateTerms, getCandidateBoldSignDocuments, generateBoldSignDocumentFromTemplate, syncBoldSignDocumentStatusAdmin } from "@/api/candidateTerms.jsx";
import { getCandidateAvailableBoldSignTemplates } from "@/api/terms.jsx";
import { Button} from "@/components/ui/button.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table.tsx";
import { toast } from "sonner";
import { Plus, X, Calendar, CheckCircle, XCircle, FileText, Download, FileSignature, RefreshCw } from "lucide-react";

export default function CandidateTermsAndAgreements({ candidateId, candidateName }) {
    const [candidateTerms, setCandidateTerms] = useState([]);
    const [boldSignTemplates, setBoldSignTemplates] = useState([]);
    const [candidateBoldSignDocuments, setCandidateBoldSignDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generatingDocuments, setGeneratingDocuments] = useState(new Set());
    const [syncingDocuments, setSyncingDocuments] = useState(new Set());

    useEffect(() => {
        loadData();
    }, [candidateId]);

    const loadData = async () => {
        setLoading(true);
        try {
            // Fetch candidate terms, available BoldSign templates, and existing documents
            const [candidateTermsData, boldSignTemplatesData, candidateBoldSignDocsData] = await Promise.all([
                getCandidateTerms(candidateId),
                getCandidateAvailableBoldSignTemplates(candidateId),
                getCandidateBoldSignDocuments(candidateId)
            ]);

            setCandidateTerms(candidateTermsData || []);
            setBoldSignTemplates(boldSignTemplatesData || []);
            setCandidateBoldSignDocuments(candidateBoldSignDocsData || []);
            
            console.log('Loaded data:', {
                terms: candidateTermsData?.length || 0,
                templates: boldSignTemplatesData?.length || 0,
                documents: candidateBoldSignDocsData?.length || 0
            });
        } catch (error) {
            console.error('Error loading candidate terms data:', error);
            toast.error('Failed to load candidate terms data');
        } finally {
            setLoading(false);
        }
    };

    // Create lookup for candidate documents by template ID
    const candidateDocsLookup = {};
    candidateBoldSignDocuments.forEach(doc => {
        candidateDocsLookup[doc.boldSignTemplateId] = doc;
    });

    // Create lookup for candidate terms by term ID
    const candidateTermsLookup = {};
    candidateTerms.forEach(ct => {
        candidateTermsLookup[ct.termId] = ct;
    });

    const handleGenerateDocument = async (templateId) => {
        setGeneratingDocuments(prev => new Set(prev.add(templateId)));
        try {
            const result = await generateBoldSignDocumentFromTemplate(candidateId, templateId);
            if (result?.success) {
                toast.success('Document generated successfully');
                await loadData();
            } else {
                toast.error(result?.error || 'Failed to generate document');
            }
        } catch (error) {
            console.error('Error generating document:', error);
            toast.error('Error generating document');
        } finally {
            setGeneratingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(templateId);
                return newSet;
            });
        }
    };

    const handleSyncDocumentStatus = async (documentId) => {
        setSyncingDocuments(prev => new Set(prev.add(documentId)));
        try {
            const result = await syncBoldSignDocumentStatusAdmin(documentId);
            if (result?.ok) {
                toast.success('Document status synced successfully');
                await loadData();
            } else {
                toast.error(result?.error || 'Failed to sync document status');
            }
        } catch (error) {
            console.error('Error syncing document status:', error);
            toast.error('Error syncing document status');
        } finally {
            setSyncingDocuments(prev => {
                const newSet = new Set(prev);
                newSet.delete(documentId);
                return newSet;
            });
        }
    };


    const getStatusBadge = (candidateDoc) => {
        if (!candidateDoc) {
            return <Badge className="bg-gray-100 text-gray-800">Not Generated</Badge>;
        }

        const waitingFor = candidateDoc.waitingFor || [];
        const boldSignStatus = candidateDoc.boldSignStatus;

        // If "candidate" is present in waiting_for
        if (waitingFor.includes('candidate')) {
            return <Badge className="bg-yellow-100 text-yellow-800">Awaiting Candidate Signature</Badge>;
        }
        
        // If "ramsden" is present in waiting_for AND "candidate" is not present
        if (waitingFor.includes('ramsden') && !waitingFor.includes('candidate')) {
            return <Badge className="bg-blue-100 text-blue-800">Awaiting Your Signature</Badge>;
        }

        // If waiting_for is empty, use bold_sign_status
        if (waitingFor.length === 0 && boldSignStatus) {
            // Map BoldSign statuses to display values
            const statusColors = {
                'Completed': 'bg-green-100 text-green-800',
                'InProgress': 'bg-yellow-100 text-yellow-800', 
                'Draft': 'bg-gray-100 text-gray-800',
                'Declined': 'bg-red-100 text-red-800',
                'Cancelled': 'bg-red-100 text-red-800'
            };
            
            const colorClass = statusColors[boldSignStatus] || 'bg-gray-100 text-gray-800';
            return <Badge className={colorClass}>{boldSignStatus}</Badge>;
        }

        // Fallback
        return <Badge className="bg-gray-100 text-gray-800">Generated</Badge>;
    };

    if (loading) {
        return (
            <Card>
                <CardContent className="p-6">
                    <p>Loading candidate terms and agreements...</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* BoldSign Documents Section */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <FileSignature className="w-5 h-5" />
                        BoldSign Documents
                        <Badge variant="secondary">{boldSignTemplates.length}</Badge>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {boldSignTemplates.length === 0 ? (
                        <p className="text-gray-500 py-4">No BoldSign templates available for this candidate.</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Template</TableHead>
                                    <TableHead>Skill</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {boldSignTemplates.map((template) => {
                                    const candidateDoc = candidateDocsLookup[template.id];
                                    const isGenerating = generatingDocuments.has(template.id);
                                    const isSyncing = candidateDoc && syncingDocuments.has(candidateDoc.id);

                                    return (
                                        <TableRow key={template.id}>
                                            <TableCell className="font-medium">
                                                {template.templateName}
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline">
                                                    {template.skillName || 'Default'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="space-y-1">
                                                    {getStatusBadge(candidateDoc)}
                                                    {candidateDoc?.createdAt && (
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(candidateDoc.createdAt).toLocaleDateString()}
                                                        </p>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-2 flex-wrap">
                                                    {!candidateDoc ? (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => handleGenerateDocument(template.id)}
                                                            disabled={isGenerating}
                                                        >
                                                            {isGenerating ? (
                                                                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                            ) : (
                                                                <Plus className="w-4 h-4 mr-1" />
                                                            )}
                                                            Generate
                                                        </Button>
                                                    ) : (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => handleSyncDocumentStatus(candidateDoc.id)}
                                                                disabled={isSyncing}
                                                            >
                                                                {isSyncing ? (
                                                                    <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                                                                ) : (
                                                                    <RefreshCw className="w-4 h-4 mr-1" />
                                                                )}
                                                                Sync Status
                                                            </Button>
                                                            
                                                            {candidateDoc.status !== 'completed' && candidateDoc.ramsdenSigningLink && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="secondary"
                                                                    onClick={() => window.open(candidateDoc.ramsdenSigningLink, '_blank')}
                                                                >
                                                                    <FileSignature className="w-4 h-4 mr-1" />
                                                                    Sign
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Terms Agreements Section - commented out for now */}
            {/*
            {candidateTerms.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Terms Agreements
                            <Badge variant="secondary">{candidateTerms.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Term</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Agreed At</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {candidateTerms.map((candidateTerm) => (
                                    <TableRow key={candidateTerm.id}>
                                        <TableCell className="font-medium">
                                            {candidateTerm.termName}
                                        </TableCell>
                                        <TableCell>
                                            {candidateTerm.agreed ? (
                                                <Badge className="bg-green-100 text-green-800">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Agreed
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-gray-100 text-gray-800">
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    Not Agreed
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-gray-600">
                                            {candidateTerm.agreedAt ? 
                                                new Date(candidateTerm.agreedAt).toLocaleDateString() : 
                                                '—'
                                            }
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
            */}
        </div>
    );
}
